/**
 * Reusable "Subscribe" button + modal pair.
 *
 * Drop into any event/venue/series surface with the matching `target` prop.
 * The modal pre-fills filters from the target, lets the user pick channels +
 * cadence, and (optionally) toggle a 1-day-before reminder + Outlook
 * calendar invite delivery. Quick mode submits with sane defaults; Customize
 * tab exposes the full filter builder.
 */
import { useEffect, useMemo, useState } from 'react';
import { useIsAuthenticated } from '@azure/msal-react';
import { notificationsApi, type CreateSubscriptionInput } from '../lib/notifications-api';
import { EVENT_TOPICS, TIME_OF_DAY_OPTIONS } from '../lib/eventTopics';
import type {
  NotificationChannel,
  Subscription,
  SubscriptionCadence,
  SubscriptionFilters,
  UserPreferences
} from '../types';

export type SubscribeTarget =
  | { kind: 'event'; id: string; title: string }
  | { kind: 'venue'; id: string; name: string }
  | { kind: 'series'; id: string; name: string }
  | { kind: 'category'; value: string }
  | { kind: 'neighborhood'; value: string }
  | { kind: 'all' };

interface Props {
  target: SubscribeTarget;
  /** Bootstrap button class. Defaults to a small outline-primary so it fits next to existing CTAs. */
  className?: string;
  /** Compact/icon-only button (used in event card grids). */
  compact?: boolean;
}

function targetLabel(t: SubscribeTarget): string {
  switch (t.kind) {
    case 'event': return t.title;
    case 'venue': return t.name;
    case 'series': return t.name;
    case 'category': return t.value;
    case 'neighborhood': return t.value;
    case 'all': return 'All Murray Hill events';
  }
}

function defaultFiltersFor(t: SubscribeTarget): SubscriptionFilters {
  switch (t.kind) {
    case 'event': return { eventIds: [t.id], lookaheadDays: 30 };
    case 'venue': return { venueIds: [t.id], lookaheadDays: 30 };
    case 'series': return { seriesIds: [t.id], lookaheadDays: 30 };
    case 'category': return { categories: [t.value], lookaheadDays: 30 };
    case 'neighborhood': return { neighborhoods: [t.value], lookaheadDays: 30 };
    case 'all': return { lookaheadDays: 30 };
  }
}

function defaultName(t: SubscribeTarget): string {
  switch (t.kind) {
    case 'event': return `Event: ${t.title}`;
    case 'venue': return `Venue: ${t.name}`;
    case 'series': return `Series: ${t.name}`;
    case 'category': return `Category: ${t.value}`;
    case 'neighborhood': return `Neighborhood: ${t.value}`;
    case 'all': return 'All events';
  }
}

export default function SubscribeButton({ target, className, compact }: Props) {
  const [open, setOpen] = useState(false);
  const isAuthed = useIsAuthenticated();
  if (!isAuthed) return null;
  const btnClass = className ?? `btn ${compact ? 'btn-sm ' : ''}btn-outline-primary`;
  const label = compact ? '🔔' : '🔔 Subscribe';
  return (
    <>
      <button type="button" className={btnClass} onClick={() => setOpen(true)} aria-label={`Subscribe to ${targetLabel(target)}`}>
        {label}
      </button>
      {open ? <SubscribeModal target={target} onClose={() => setOpen(false)} /> : null}
    </>
  );
}

interface ModalProps {
  target: SubscribeTarget;
  onClose: () => void;
}

const ALL_CHANNELS: NotificationChannel[] = ['sms', 'email', 'calendarInvite'];

/**
 * Available reminder offsets, in hours before the event start. Backend
 * accepts any integer 1–336, but presenting a curated list keeps the UI
 * clean. Users can stack multiple offsets per subscription.
 */
const REMINDER_OFFSET_PRESETS: Array<{ hours: number; label: string }> = [
  { hours: 1, label: '1 hour' },
  { hours: 3, label: '3 hours' },
  { hours: 6, label: '6 hours' },
  { hours: 12, label: '12 hours' },
  { hours: 24, label: '1 day' },
  { hours: 48, label: '2 days' },
  { hours: 168, label: '1 week' }
];

function SubscribeModal({ target, onClose }: ModalProps) {
  const [tab, setTab] = useState<'quick' | 'customize'>('quick');
  const [name, setName] = useState(defaultName(target));
  const [filters, setFilters] = useState<SubscriptionFilters>(defaultFiltersFor(target));
  const [channels, setChannels] = useState<NotificationChannel[]>(['email']);
  // Per-event subscriptions default to "Reminders only" — most users
  // following a single event just want the 24h reminder, not instant pings.
  const [cadence, setCadence] = useState<SubscriptionCadence>(target.kind === 'event' ? 'none' : 'daily');
  // Reminder offsets (hours before event start). Default to 24h when this is
  // a single-event sub; empty for category/series subs since cadence covers it.
  const [reminderOffsets, setReminderOffsets] = useState<number[]>(target.kind === 'event' ? [24] : []);
  const [sendCalendarInvite, setSendCalendarInvite] = useState(target.kind === 'event');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [created, setCreated] = useState<Subscription | null>(null);

  const [prefs, setPrefs] = useState<UserPreferences | null>(null);
  useEffect(() => {
    let cancel = false;
    notificationsApi.getPreferences().then(p => { if (!cancel) setPrefs(p); }).catch(() => {});
    return () => { cancel = true; };
  }, []);

  const smsAvailable = !!prefs?.phone && !!prefs?.phoneVerifiedAt && !prefs?.smsOptOut;
  const emailAvailable = !!prefs?.email && !prefs?.emailOptOut;

  const channelHelp: Record<NotificationChannel, string | null> = useMemo(() => ({
    sms: smsAvailable ? null : 'Verify a phone number on your Notifications page first.',
    email: emailAvailable ? null : 'Add or re-enable your email on your Notifications page first.',
    calendarInvite: emailAvailable ? null : 'Calendar invites are sent to your email; enable email first.'
  }), [smsAvailable, emailAvailable]);

  function toggleChannel(c: NotificationChannel) {
    setChannels(cs => cs.includes(c) ? cs.filter(x => x !== c) : [...cs, c]);
  }

  async function submit() {
    setSubmitting(true);
    setError(null);
    try {
      const input: CreateSubscriptionInput = {
        name: name.trim() || defaultName(target),
        cadence,
        channels,
        filters,
        reminderOffsets,
        // Keep the legacy field in sync for any consumer still reading it.
        reminderDayBefore: reminderOffsets.length > 0,
        sendCalendarInviteOnMatch: sendCalendarInvite && channels.includes('calendarInvite')
      };
      const sub = await notificationsApi.create(input);
      setCreated(sub);
    } catch (e: any) {
      setError(e?.message ?? 'Failed to create subscription.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <>
      <div className="modal-backdrop fade show" />
      <div className="modal fade show d-block" tabIndex={-1} role="dialog" aria-modal="true" aria-labelledby="subscribe-modal-title">
      <div className="modal-dialog modal-dialog-centered modal-lg" role="document">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title" id="subscribe-modal-title">Subscribe to {targetLabel(target)}</h5>
            <button type="button" className="btn-close" aria-label="Close" onClick={onClose}></button>
          </div>
          <div className="modal-body">
            {created ? (
              <div className="alert alert-success" role="status">
                Subscription created.{' '}
                <a href="/notifications">Manage notifications</a>
              </div>
            ) : (
              <>
                <ul className="nav nav-tabs mb-3" role="tablist">
                  <li className="nav-item" role="presentation">
                    <button type="button" className={`nav-link ${tab === 'quick' ? 'active' : ''}`} onClick={() => setTab('quick')} role="tab" aria-selected={tab === 'quick'}>Quick</button>
                  </li>
                  <li className="nav-item" role="presentation">
                    <button type="button" className={`nav-link ${tab === 'customize' ? 'active' : ''}`} onClick={() => setTab('customize')} role="tab" aria-selected={tab === 'customize'}>Customize filters</button>
                  </li>
                </ul>

                <div className="mb-3">
                  <label htmlFor="sub-name" className="form-label">Name</label>
                  <input id="sub-name" className="form-control" value={name} onChange={e => setName(e.target.value)} maxLength={120} />
                </div>

                <fieldset className="mb-3">
                  <legend className="form-label h6">Channels</legend>
                  {ALL_CHANNELS.map(c => (
                    <div key={c} className="form-check">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        id={`sub-ch-${c}`}
                        checked={channels.includes(c)}
                        onChange={() => toggleChannel(c)}
                      />
                      <label htmlFor={`sub-ch-${c}`} className="form-check-label">
                        {c === 'sms' ? 'Text message (SMS)' : c === 'email' ? 'Email' : 'Outlook calendar invite'}
                      </label>
                      {channelHelp[c] ? <div className="small text-muted">{channelHelp[c]}</div> : null}
                    </div>
                  ))}
                </fieldset>

                <div className="mb-3">
                  <label htmlFor="sub-cadence" className="form-label">Cadence</label>
                  <select id="sub-cadence" className="form-select" value={cadence} onChange={e => setCadence(e.target.value as SubscriptionCadence)}>
                    <option value="instant">Instant — as soon as we find a match</option>
                    <option value="daily">Daily digest (8 AM)</option>
                    <option value="weekly">Weekly digest (Mon 8 AM)</option>
                    <option value="none">Reminders only</option>
                  </select>
                </div>

                <fieldset className="mb-2">
                  <legend className="form-label h6 mb-1">Reminders before event</legend>
                  <div className="d-flex flex-wrap gap-1" role="group" aria-label="Reminder offsets">
                    {REMINDER_OFFSET_PRESETS.map(({ hours, label }) => {
                      const selected = reminderOffsets.includes(hours);
                      return (
                        <button
                          key={hours}
                          type="button"
                          className={`btn btn-sm ${selected ? 'btn-primary' : 'btn-outline-secondary'}`}
                          aria-pressed={selected}
                          onClick={() => setReminderOffsets(curr =>
                            curr.includes(hours)
                              ? curr.filter(h => h !== hours)
                              : [...curr, hours].sort((a, b) => a - b)
                          )}
                        >
                          {label}
                        </button>
                      );
                    })}
                  </div>
                  <div className="form-text">Pick one or more reminders. Leave blank for no reminders.</div>
                </fieldset>
                <div className="form-check mb-3">
                  <input type="checkbox" className="form-check-input" id="sub-invite" checked={sendCalendarInvite} onChange={e => setSendCalendarInvite(e.target.checked)} disabled={!channels.includes('calendarInvite')} />
                  <label htmlFor="sub-invite" className="form-check-label">Also send an Outlook calendar invite for each match</label>
                </div>

                {tab === 'customize' ? <FiltersBuilder filters={filters} onChange={setFilters} /> : null}

                {error ? <div className="alert alert-danger" role="alert">{error}</div> : null}
              </>
            )}
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>{created ? 'Close' : 'Cancel'}</button>
            {!created ? (
              <button type="button" className="btn btn-primary" onClick={submit} disabled={submitting || channels.length === 0}>
                {submitting ? 'Saving…' : 'Subscribe'}
              </button>
            ) : null}
          </div>
        </div>
      </div>
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// Filter builder (used only inside the Customize tab)
// ---------------------------------------------------------------------------

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

interface FiltersBuilderProps {
  filters: SubscriptionFilters;
  onChange: (next: SubscriptionFilters) => void;
}

function FiltersBuilder({ filters, onChange }: FiltersBuilderProps) {
  const mask = filters.dayOfWeekMask ?? 0;
  function toggleDay(i: number) {
    const next = mask ^ (1 << i);
    onChange({ ...filters, dayOfWeekMask: next === 0 ? undefined : next });
  }
  function csv(arr: string[] | undefined): string { return (arr ?? []).join(', '); }
  function setCsv(key: keyof SubscriptionFilters, v: string) {
    const list = v.split(',').map(s => s.trim()).filter(Boolean);
    onChange({ ...filters, [key]: list.length ? list : undefined });
  }
  function toggleIn(key: 'topics' | 'timesOfDay', value: string) {
    const curr = filters[key] ?? [];
    const next = curr.includes(value) ? curr.filter(v => v !== value) : [...curr, value];
    onChange({ ...filters, [key]: next.length ? next : undefined });
  }
  return (
    <div className="border-top pt-3 mt-2">
      <div className="row g-3">
        <div className="col-md-6">
          <label htmlFor="f-keywords-include" className="form-label">Must include keywords</label>
          <input id="f-keywords-include" className="form-control" placeholder="comma-separated"
            value={csv(filters.keywordIncludes)}
            onChange={e => setCsv('keywordIncludes', e.target.value)} />
        </div>
        <div className="col-md-6">
          <label htmlFor="f-keywords-exclude" className="form-label">Exclude keywords</label>
          <input id="f-keywords-exclude" className="form-control" placeholder="comma-separated"
            value={csv(filters.keywordExcludes)}
            onChange={e => setCsv('keywordExcludes', e.target.value)} />
        </div>
        <div className="col-12">
          <fieldset>
            <legend className="form-label">Topics</legend>
            <div className="d-flex flex-wrap gap-1" role="group" aria-label="Topics">
              {EVENT_TOPICS.map(t => {
                const on = (filters.topics ?? []).includes(t.id);
                return (
                  <button
                    key={t.id}
                    type="button"
                    aria-pressed={on}
                    className={`btn btn-sm ${on ? 'btn-primary' : 'btn-outline-secondary'}`}
                    onClick={() => toggleIn('topics', t.id)}
                  >{t.icon} {t.label}</button>
                );
              })}
            </div>
            <div className="form-text">Leave all off for any topic.</div>
          </fieldset>
        </div>
        <div className="col-md-6">
          <label htmlFor="f-tags" className="form-label">Tags</label>
          <input id="f-tags" className="form-control" placeholder="family-friendly, outdoors…"
            value={csv(filters.tags)}
            onChange={e => setCsv('tags', e.target.value)} />
        </div>
        <div className="col-md-6">
          <label htmlFor="f-neighborhoods" className="form-label">Neighborhoods</label>
          <input id="f-neighborhoods" className="form-control" placeholder="Murray Hill, Riverside…"
            value={csv(filters.neighborhoods)}
            onChange={e => setCsv('neighborhoods', e.target.value)} />
        </div>
        <div className="col-md-6">
          <fieldset>
            <legend className="form-label">Days of week</legend>
            <div className="btn-group" role="group" aria-label="Days of week">
              {DAYS.map((d, i) => {
                const on = (mask & (1 << i)) !== 0;
                return (
                  <button
                    key={d}
                    type="button"
                    aria-pressed={on}
                    className={`btn btn-sm ${on ? 'btn-primary' : 'btn-outline-secondary'}`}
                    onClick={() => toggleDay(i)}
                  >{d}</button>
                );
              })}
            </div>
          </fieldset>
        </div>
        <div className="col-md-6">
          <fieldset>
            <legend className="form-label">Time of day</legend>
            <div className="d-flex flex-wrap gap-1" role="group" aria-label="Time of day">
              {TIME_OF_DAY_OPTIONS.map(o => {
                const on = (filters.timesOfDay ?? []).includes(o.value);
                return (
                  <button
                    key={o.value}
                    type="button"
                    aria-pressed={on}
                    className={`btn btn-sm ${on ? 'btn-primary' : 'btn-outline-secondary'}`}
                    onClick={() => toggleIn('timesOfDay', o.value)}
                  >{o.icon} {o.label}</button>
                );
              })}
            </div>
            <div className="form-text">By start time. All-day and time-TBA events only come through when this is left blank.</div>
          </fieldset>
        </div>
        <div className="col-md-3">
          <label htmlFor="f-age" className="form-label">Max age restriction</label>
          <select id="f-age" className="form-select"
            value={filters.ageRestrictionMax ?? ''}
            onChange={e => onChange({ ...filters, ageRestrictionMax: (e.target.value || undefined) as SubscriptionFilters['ageRestrictionMax'] })}>
            <option value="">Any</option>
            <option value="all-ages">All-ages only</option>
            <option value="18+">18+ or under</option>
            <option value="21+">21+ or under</option>
          </select>
        </div>
        <div className="col-md-3">
          <label htmlFor="f-lookahead" className="form-label">Lookahead (days)</label>
          <input id="f-lookahead" type="number" min={1} max={60} className="form-control"
            value={filters.lookaheadDays ?? 30}
            onChange={e => onChange({ ...filters, lookaheadDays: Math.max(1, Math.min(60, Number(e.target.value) || 30)) })} />
        </div>
        <div className="col-md-3">
          <div className="form-check mt-4">
            <input type="checkbox" className="form-check-input" id="f-free"
              checked={!!filters.freeOnly}
              onChange={e => onChange({ ...filters, freeOnly: e.target.checked || undefined })} />
            <label htmlFor="f-free" className="form-check-label">Free events only</label>
          </div>
        </div>
      </div>
    </div>
  );
}
